import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { deleteAccount as deleteAccountService } from '../services/account';

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  assistantName: 'Alex' | 'Sam';
  createdAt: Date;
  subscriptionTier: 'free' | 'pro' | 'enterprise';
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

function parseCreatedAt(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (value instanceof Timestamp) {
    const parsed = value.toDate();
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === 'object') {
    const v = value as { seconds?: unknown; _seconds?: unknown; nanoseconds?: unknown; _nanoseconds?: unknown };
    const maybeSeconds = typeof v.seconds === 'number' ? v.seconds : typeof v._seconds === 'number' ? v._seconds : undefined;
    const maybeNanos =
      typeof v.nanoseconds === 'number' ? v.nanoseconds : typeof v._nanoseconds === 'number' ? v._nanoseconds : undefined;

    if (typeof maybeSeconds === 'number') {
      const msFromSeconds = maybeSeconds * 1000;
      const msFromNanos = typeof maybeNanos === 'number' ? Math.floor(maybeNanos / 1_000_000) : 0;
      const parsed = new Date(msFromSeconds + msFromNanos);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  return null;
}

function normalizeUserProfile(data: unknown): UserProfile | null {
  if (!data || typeof data !== 'object') return null;

  const obj = data as Partial<UserProfile> & { createdAt?: unknown };
  const createdAt = parseCreatedAt(obj.createdAt) ?? new Date();

  if (!obj.uid || !obj.email || !obj.displayName || !obj.assistantName || !obj.subscriptionTier) {
    return null;
  }

  return {
    uid: obj.uid,
    email: obj.email,
    displayName: obj.displayName,
    assistantName: obj.assistantName,
    subscriptionTier: obj.subscriptionTier,
    createdAt,
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🎯 Setting up onAuthStateChanged listener');
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🎯 onAuthStateChanged fired with user:', firebaseUser?.email);
      setUser(firebaseUser);

      if (firebaseUser) {
        // If emulator data was reset but the browser still has persisted auth state,
        // Firebase can surface an invalid refresh token on startup. Treat it as a logged-out state.
        try {
          await firebaseUser.getIdToken(false);
        } catch (err: any) {
          const code = err?.code as string | undefined;
          if (code === 'auth/invalid-refresh-token' || code === 'auth/user-token-expired') {
            console.warn('[Auth] Token invalid/expired; signing out to recover:', code);
            try {
              await firebaseSignOut(auth);
            } catch {
              // ignore
            }
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            return;
          }
        }

        // Fetch user profile from Firestore
        const profileDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (profileDoc.exists()) {
          console.log('🎯 User profile found:', profileDoc.data());
          const normalized = normalizeUserProfile(profileDoc.data());
          setUserProfile(normalized);
        }
      } else {
        console.log('🎯 No user, clearing profile');
        setUserProfile(null);
      }

      console.log('🎯 Setting loading to false');
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      console.log('🔐 SignUp: Starting signup for', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('🔐 SignUp: User created, uid:', userCredential.user.uid);

      // Update Firebase Auth profile
      await updateProfile(userCredential.user, { displayName });
      console.log('🔐 SignUp: Profile updated');

      // Create user profile in Firestore
      const profile: UserProfile = {
        uid: userCredential.user.uid,
        email: userCredential.user.email!,
        displayName,
        assistantName: 'Alex', // Default assistant
        createdAt: new Date(),
        subscriptionTier: 'free',
      };

      try {
        await setDoc(doc(db, 'users', userCredential.user.uid), profile);
        console.log('🔐 SignUp: Firestore profile created');
      } catch (firestoreError) {
        console.warn('Could not create Firestore profile:', firestoreError);
        // Continue anyway - user can still use the app
      }
      
      // For emulator: manually update state immediately while waiting for listener
      setUser(userCredential.user);
      setUserProfile(profile);
      setLoading(false);
      console.log('🔐 SignUp: Complete - waiting for onAuthStateChanged...');
    } catch (error: any) {
      console.error('🔐 SignUp Error:', error.message || error);
      setLoading(false);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Manually update state since emulator may not trigger onAuthStateChanged
      setUser(userCredential.user);
      
      // Fetch user profile from Firestore - don't fail if profile fetch fails
      try {
        const profileDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
        if (profileDoc.exists()) {
          setUserProfile(normalizeUserProfile(profileDoc.data()));
        }
      } catch (firestoreError) {
        console.warn('Could not fetch user profile:', firestoreError);
        // Continue anyway - user profile is optional for basic login
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    console.log('🔐 Google Sign-In: Starting...');
    console.log('🔐 Auth instance:', auth);
    console.log('🔐 Auth config:', (auth as any)?.config);
    try {
      const provider = new GoogleAuthProvider();
      console.log('🔐 Google Sign-In: Provider created, calling signInWithPopup...');
      const userCredential = await signInWithPopup(auth, provider);
      console.log('🔐 Google Sign-In: Success!', userCredential.user.email);

      // Check if user profile exists
      try {
        const profileDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

        if (!profileDoc.exists()) {
          const profile: UserProfile = {
            uid: userCredential.user.uid,
            email: userCredential.user.email!,
            displayName: userCredential.user.displayName || 'User',
            assistantName: 'Alex',
            createdAt: new Date(),
            subscriptionTier: 'free',
          };

          await setDoc(doc(db, 'users', userCredential.user.uid), profile);
          setUserProfile(profile);
        } else {
          setUserProfile(normalizeUserProfile(profileDoc.data()));
        }
      } catch (firestoreError) {
        console.warn('Could not fetch/create user profile for Google sign-in:', firestoreError);
      }

      setUser(userCredential.user);
      setLoading(false);
    } catch (error: any) {
      console.error('🔐 Google Sign-In ERROR:', error.code, error.message, error);
      setLoading(false);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      // Manually clear state to handle emulator issues
      setUser(null);
      setUserProfile(null);
      setLoading(false);
    } catch (error: any) {
      console.error('Sign out error:', error);
      // Still clear state even if Firebase call fails
      setUser(null);
      setUserProfile(null);
      throw error;
    }
  };

  const deleteAccount = async () => {
    try {
      await deleteAccountService();
    } catch (error: any) {
      console.error('Delete account error:', error);
      throw error;
    } finally {
      setUser(null);
      setUserProfile(null);
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
